import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Body of POST `${environment.apiUrl}support/contact` — the helpdesk intake
 * behind the /contact-us form.
 */
export interface SupportContactRequest {
  /**
   * Why they're writing in — one of the machine values in `CONTACT_REASONS`
   * (`sales`, `support`, …). The backend routes on it: only some reasons open
   * a helpdesk ticket, the rest are relayed by email.
   */
  reason: string;
  name: string;
  email: string;
  subject: string;
  message: string;
  phone: string;
  company: string;
  /**
   * Honeypot. Rendered offscreen and never filled by a human, so the backend
   * treats a non-empty value as a bot. Always sent (empty for real people).
   */
  website: string;
}

export interface SupportContactResponse {
  status: boolean;
  code: number;
  message: string;
  data: {
    received: boolean;
    /**
     * Helpdesk ticket reference, e.g. `TKT-000123`. Optional: reasons that
     * don't open a ticket come back as a bare `{ received: true }`, as does a
     * submission the backend silently swallows (a filled honeypot still gets a
     * 200). Callers must treat its absence as success, not as a failure.
     */
    reference?: string;
  };
}

@Injectable({
  providedIn: 'root',
})
export class SupportContactService {
  private http = inject(HttpClient);

  /**
   * Credential-less submit. The global HttpClientInterceptor stamps
   * `Accept-Language`, so 422 messages come back already localized.
   */
  submit(payload: SupportContactRequest): Observable<SupportContactResponse> {
    return this.http.post<SupportContactResponse>(
      `${environment.apiUrl}support/contact`,
      payload,
    );
  }
}
